import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { InlineNotification, Loading } from 'carbon-components-react';
import { loadStripe } from '@stripe/stripe-js';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { WizardShell } from 'components/PremiumWizard';
import useBelWizard from './hooks/useBelWizard';
import PriceStep from './components/PriceStep';
import UploadStep from './components/UploadStep';
import UploadPipelineOverlay from './components/UploadPipelineOverlay';
import DetailsStep from './components/DetailsStep';
import CompleteStep from './components/CompleteStep';
import PaymentStep from './components/PaymentStep';
import CheckoutStep from './components/CheckoutStep';
import { RootState } from 'redux/root.reducer';
import {
  fetchLivePriceStart,
  confirmBelPayment,
  verifyBelPayment,
  clearGeometryPipelineError
} from 'redux/bulkEnquiryLodgement/actions';
import {
  selectPostPaymentConfirmed,
  selectConfirmingPostPayment
} from 'redux/payment/selectors';
import { getSystemFileDownloadUrl } from 'services/bulkEnquiryLodgement.service';
import { selectUser, selectUsersLoading } from 'redux/user/selector';
import { updateProfileUpdatingFlag } from 'redux/user/actions';
import { setHeaderPanelExpand } from 'redux/app/actions';
import { APP_URL } from 'types';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

const PRIMARY_LABELS: { [key: string]: string } = {
  price: 'Continue',
  upload: 'Continue',
  details: 'Continue'
};

const BulkEnquiryLodgement: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const postPaymentConfirmed = useSelector(selectPostPaymentConfirmed);
  const confirmingPostPayment = useSelector(selectConfirmingPostPayment);
  const user = useSelector(selectUser);
  const isUserLoading = useSelector(selectUsersLoading);

  useEffect(() => {
    dispatch(fetchLivePriceStart());
  }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentComplete = params.get('payment');
    const sessionId = params.get('session_id');

    if (paymentComplete === 'complete' && sessionId) {
      window.history.replaceState({}, document.title, window.location.pathname);
      dispatch(verifyBelPayment(sessionId));
    }
  }, [dispatch]);

  useEffect(() => {
    if (postPaymentConfirmed) {
      dispatch(confirmBelPayment());
    }
  }, [postPaymentConfirmed, dispatch]);

  const {
    activeStep,
    steps,
    uploadedFile,
    enquiryDetails,
    setEnquiryDetails,
    goNext,
    goBack,
    resetWizard,
    resetUploadOnly,
    canProceed,
    loading,
    error,
    processingPhase,
    onFileSelected,
    totalFeatures,
    totalAreaSqm,
    bufferDistance,
    maxSplitAreaKm,
    applyBuffer,
    applySplit,
    onBufferDistanceChange,
    onMaxSplitAreaChange,
    onApplyBufferChange,
    onApplySplitChange,
    onGeometryInputsValidityChange,
    validationSummary,
    geometrySettings,
    geometrySettingsValidation,
    geometryValidationErrorBanner,
    paymentStatus,
    purchaseOrderNumber,
    onPurchaseOrderNumberChange,
    checkoutClientSecret,
    onConfirmPayment
  } = useBelWizard();

  useEffect(() => {
    return () => {
      resetWizard();
    };
  }, [resetWizard]);

  const {
    stripePrice,
    livePriceLoading,
    livePriceError,
    enquiriesLodged,
    totalEnquiriesCreated,
    totalBulkEnquiries,
    isPostpay,
    transactionReference,
    updatedFileId,
    bulkStatus
  } = useSelector((s: RootState) => s.belCache);

  const geometryPipelineFailed = useSelector(
    (s: RootState) => s.belCache.geometryPipelineFailed
  );

  const isPostPaymentPolling = paymentStatus === 'confirmed' && loading;

  const displayedEnquiriesLodged = isPostPaymentPolling
    ? totalBulkEnquiries ?? totalEnquiriesCreated ?? 0
    : enquiriesLodged ?? totalBulkEnquiries ?? totalEnquiriesCreated ?? 0;
  const currentStepId = steps[activeStep]?.id;
  const [isDownloading, setIsDownloading] = useState(false);

  const handleStartNew = useCallback(() => {
    resetWizard();
  }, [resetWizard]);

  const handleMyEnquiries = useCallback(() => {
    history.push(`${APP_URL.ENQUIRIY_HISTORY}?view=list`);
  }, [history]);

  const handleDownload = useCallback(async () => {
    if (!updatedFileId) return;

    setIsDownloading(true);
    try {
      const { url } = await getSystemFileDownloadUrl(updatedFileId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[BEL] Failed to fetch download URL', err); // eslint-disable-line no-console
    } finally {
      setIsDownloading(false);
    }
  }, [updatedFileId]);

  const handleOpenProfile = useCallback(() => {
    dispatch(updateProfileUpdatingFlag(true));
    dispatch(setHeaderPanelExpand({ panel: 'account', isOpen: false }));
  }, [dispatch]);

  const showLodgingScreen = paymentStatus === 'confirmed' && loading;
  const isPreparingExport = showLodgingScreen && bulkStatus === 'COMPLETED';

  const renderStepContent = () => {
    switch (currentStepId) {
      case 'price':
        return (
          <PriceStep
            stripePrice={stripePrice}
            isLivePriceLoading={livePriceLoading}
            livePriceError={livePriceError}
            missingProfileAddress={!isUserLoading && !user?.Address?.line1}
            onOpenProfile={handleOpenProfile}
          />
        );
      case 'upload':
        return (
          <div className="bel-upload-step-wrapper">
            {loading && processingPhase === 'PROCESSING' && (
              <UploadPipelineOverlay variant="processing" />
            )}
            {geometryPipelineFailed && (
              <UploadPipelineOverlay
                variant="error"
                onClose={() => dispatch(clearGeometryPipelineError())}
              />
            )}
            <UploadStep
              uploadedFile={uploadedFile}
              isUploading={processingPhase === 'UPLOADING_FILE'}
              isValidating={
                processingPhase === 'TRANSFORMING_FILE' ||
                processingPhase === 'VALIDATING_FILE'
              }
              error={
                currentStepId === 'upload' && !geometryPipelineFailed
                  ? error
                  : null
              }
              onFileSelected={onFileSelected}
              onFileReplace={resetUploadOnly}
              totalFeatures={totalFeatures}
              geometryTypes={validationSummary?.geometryTypes ?? []}
              totalAreaSqm={totalAreaSqm}
              bufferDistance={bufferDistance}
              maxSplitAreaKm={maxSplitAreaKm}
              applyBuffer={applyBuffer}
              applySplit={applySplit}
              geometrySettings={geometrySettings}
              geometryValidationErrorBanner={geometryValidationErrorBanner}
              geometrySettingsCanProceed={geometrySettingsValidation.canProceed}
              isGeometryValidationLoading={geometrySettingsValidation.loading}
              onBufferDistanceChange={onBufferDistanceChange}
              onMaxSplitAreaChange={onMaxSplitAreaChange}
              onApplyBufferChange={onApplyBufferChange}
              onApplySplitChange={onApplySplitChange}
              onGeometryInputsValidityChange={onGeometryInputsValidityChange}
            />
          </div>
        );
      case 'details':
        return (
          <div className="bel-details-step-wrapper">
            {loading && (
              <Loading description="Preparing your file…" withOverlay />
            )}
            <DetailsStep
              details={enquiryDetails}
              validationSummary={validationSummary}
              onChange={setEnquiryDetails}
            />
          </div>
        );
      case 'complete': {
        const paymentPending =
          paymentStatus === 'invoice' ||
          paymentStatus === 'checkout' ||
          paymentStatus === 'creating' ||
          paymentStatus === 'failed';

        if (
          paymentPending &&
          paymentStatus === 'checkout' &&
          checkoutClientSecret
        ) {
          return (
            <CheckoutStep
              clientSecret={checkoutClientSecret}
              stripePromise={stripePromise}
            />
          );
        }

        if (paymentPending) {
          const isPaymentStepLoading =
            paymentStatus === 'creating' ||
            !!confirmingPostPayment ||
            (postPaymentConfirmed && paymentStatus === 'invoice');

          return (
            <div className="bel-payment-step-wrapper">
              {isPaymentStepLoading && (
                <Loading
                  description={
                    confirmingPostPayment
                      ? 'Confirming your invoice…'
                      : 'Preparing your invoice…'
                  }
                  withOverlay
                />
              )}
              <PaymentStep
                stripePrice={stripePrice}
                error={paymentStatus === 'failed' ? error : null}
                purchaseOrderNumber={purchaseOrderNumber}
                onPurchaseOrderNumberChange={onPurchaseOrderNumberChange}
              />
            </div>
          );
        }

        if (showLodgingScreen) {
          return (
            <div className="bel-lodging-step">
              <div className="bel-lodging-step__card">
                <Loading withOverlay={false} />
                <h2 className="bel-lodging-step__heading">
                  {isPreparingExport
                    ? 'Preparing your updated file'
                    : 'Lodging your enquiries'}
                </h2>
                <p className="bel-lodging-step__message">
                  {isPreparingExport
                    ? 'Your enquiries are lodged. We are preparing your updated geospatial file for download.'
                    : "You can close this page. We'll notify you by email once your enquiries have been lodged."}
                </p>
              </div>
            </div>
          );
        }

        if (processingPhase === 'TIMEOUT') {
          return (
            <div className="bel-timeout-step">
              <InlineNotification
                kind="warning"
                title="Your large area enquiries are taking longer than expected"
                subtitle={
                  <>
                    You can leave this page while we finish lodging the
                    enquiries. We&apos;ll email you when they&apos;re ready.
                    <br />
                    If you need help, contact{' '}
                    <a href="mailto:support@byda.com.au">support@byda.com.au</a>
                  </>
                }
                lowContrast
                hideCloseButton
              />
            </div>
          );
        }

        return (
          <CompleteStep
            enquiriesLodged={displayedEnquiriesLodged}
            isPostpay={isPostpay}
            transactionReference={transactionReference}
            onDownload={handleDownload}
            onMyEnquiries={handleMyEnquiries}
            onStartNew={handleStartNew}
            isDownloadDisabled={!updatedFileId}
            isDownloading={isDownloading}
          />
        );
      }
      default:
        return null;
    }
  };

  const isPaymentPhase =
    currentStepId === 'complete' &&
    (paymentStatus === 'invoice' ||
      paymentStatus === 'checkout' ||
      paymentStatus === 'creating' ||
      paymentStatus === 'failed');
  const isCheckoutPhase =
    currentStepId === 'complete' &&
    paymentStatus === 'checkout' &&
    Boolean(checkoutClientSecret);
  const isProcessingAfterPayment =
    currentStepId === 'complete' && paymentStatus === 'confirmed' && loading;

  const isLivePriceLoadingOnPriceStep =
    currentStepId === 'price' && livePriceLoading;

  const isUploadPipelineProcessing =
    currentStepId === 'upload' && processingPhase === 'PROCESSING' && loading;

  const getPrimaryLabel = () => {
    if (isPaymentPhase && !isCheckoutPhase) return 'Confirm';
    if (isProcessingAfterPayment) return 'Processing...';
    if (isUploadPipelineProcessing) return 'Processing...';
    return PRIMARY_LABELS[currentStepId] || 'Continue';
  };

  const getPrimaryOnClick = () => {
    if (isPaymentPhase && !isCheckoutPhase) return onConfirmPayment;
    return goNext;
  };

  const getPrimaryDisabled = () => {
    if (isCheckoutPhase) return true;
    if (isProcessingAfterPayment) return true;
    if (isUploadPipelineProcessing) return true;
    if (isPaymentPhase && paymentStatus === 'creating') return true;
    if (isPaymentPhase && paymentStatus === 'invoice') return false;
    if (isPaymentPhase && paymentStatus === 'failed') return false;
    if (
      currentStepId === 'upload' &&
      geometrySettingsValidation.pendingStepAdvance
    )
      return false;
    if (
      currentStepId === 'complete' &&
      paymentStatus === 'confirmed' &&
      !loading
    )
      return true;
    return !canProceed || (currentStepId === 'details' && loading);
  };

  const hidePrimaryAction =
    isCheckoutPhase ||
    isProcessingAfterPayment ||
    isUploadPipelineProcessing ||
    confirmingPostPayment ||
    (currentStepId === 'complete' && paymentStatus === 'confirmed' && !loading);

  const showBack =
    activeStep > 0 &&
    currentStepId !== 'details' &&
    !isUploadPipelineProcessing &&
    (currentStepId !== 'complete' ||
      (currentStepId === 'complete' && paymentStatus === 'invoice')) &&
    !confirmingPostPayment;

  const showGlobalError =
    error && currentStepId !== 'upload' && currentStepId !== 'complete';

  return (
    <div data-testid="large-area-enquiries">
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="Large area enquiries"
        pageDescription="Lodge multiple enquiries using a geospatial data file. "
        enableHeaderGlobalBar={true}
      >
        <WizardShell
          title="Large area enquiries"
          subtitle="Lodge multiple enquiries using a geospatial data file. "
          steps={steps}
          currentStepIndex={activeStep}
          isLastStepCompleted={processingPhase === 'COMPLETED'}
          onBack={showBack ? goBack : undefined}
          showBack={showBack}
          primaryAction={{
            label: getPrimaryLabel(),
            onClick: getPrimaryOnClick(),
            disabled: getPrimaryDisabled(),
            loading:
              isProcessingAfterPayment ||
              isUploadPipelineProcessing ||
              isLivePriceLoadingOnPriceStep ||
              (currentStepId === 'details' && loading) ||
              (currentStepId === 'upload' &&
                geometrySettingsValidation.loading),
            show: !hidePrimaryAction
          }}
        >
          {showGlobalError && (
            <div
              className="bel-upload-step__notification"
              style={{ marginBottom: '1rem' }}
            >
              <InlineNotification
                kind="error"
                title=""
                subtitle={error}
                lowContrast
                hideCloseButton
              />
            </div>
          )}
          {renderStepContent()}
        </WizardShell>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

export default BulkEnquiryLodgement;
